import React, { useState } from 'react';
import { X, ChevronsDown } from 'lucide-react';
// Tokens carry a per-doctor prefix; show them the way the patient's receipt does.
import { formatVisitToken } from './TokenPrintModal';
import useDialog from '../hooks/useDialog';

// Shared defer-visit modal. Used by NurseStation (defers a no-show before vitals),
// PrescriptionManagement (doctor defers a no-show consultation), and TokenManagement.
// `afterVisitId` semantics match the backend's POST /visits/{id}/defer:
//   null  → move to the end of the queue
//   uuid  → place this visit right after the chosen anchor
const DeferVisitModal = ({ visit, queue, onClose, onConfirm }) => {
  const patient = visit.patient || {};
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'this patient';

  const others = (queue || []).filter(v => v.id !== visit.id);

  const [mode, setMode] = useState('end');
  const [afterId, setAfterId] = useState(others[0]?.id || null);
  const deferDialogRef = useDialog(onClose, { confirmDiscard: false });

  const handleConfirm = () => {
    onConfirm(mode === 'after' ? afterId : null);
  };

  return (
    <div
      ref={deferDialogRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Defer {name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Where should they go in the queue?</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
            mode === 'end' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="deferMode"
              value="end"
              checked={mode === 'end'}
              onChange={() => setMode('end')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900 flex items-center gap-2">
                <ChevronsDown className="h-4 w-4 text-emerald-600" />
                Move to the end of the queue
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Patient will be called again only after every other waiting patient.
              </p>
            </div>
          </label>

          <div className={`p-4 border-2 rounded-xl transition-colors ${
            mode === 'after' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
          }`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="deferMode"
                value="after"
                checked={mode === 'after'}
                onChange={() => setMode('after')}
                disabled={others.length === 0}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Move behind a specific patient</div>
                <p className="text-xs text-gray-500 mt-1">
                  Patient will be called right after the one you select.
                </p>
              </div>
            </label>
            {mode === 'after' && others.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-60 overflow-y-auto pl-7">
                {others.map(v => {
                  const p = v.patient || {};
                  const pName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown';
                  const isSelected = afterId === v.id;
                  return (
                    <label
                      key={v.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? 'bg-emerald-100' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="afterId"
                        checked={isSelected}
                        onChange={() => setAfterId(v.id)}
                        className="flex-shrink-0"
                      />
                      <span className="font-mono text-xs text-gray-500 min-w-[2.5rem]">{formatVisitToken(v)}</span>
                      <span className="font-medium text-gray-900 flex-1 truncate">{pName}</span>
                      <span className="text-xs text-gray-400">{p.mrn}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {others.length === 0 && (
              <p className="text-xs text-gray-400 mt-2 pl-7">
                No other patients in the queue - this option isn't available.
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={mode === 'after' && !afterId}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Defer
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeferVisitModal;
